/** The team lens.
 *
 * A chip per team with its project count, and beneath it the selected team's categories. It
 * **filters in place and never hides**: "All teams" is always one click away, and nothing is
 * buried in a submenu. That rule is inherited verbatim from the pilot, where it existed for a
 * reason — losing sight of work is the failure this tool was built to prevent.
 *
 * The counts are not decoration. A chip reading "IT Operations 28" tells someone where the work
 * actually is; a category reading 0 is a question worth asking out loud.
 *
 * Selection lives in the URL, so a filtered view can be sent to someone and arrives filtered.
 */

import { Loading, Pill } from '@/components/ui'
import { useTeams, type TeamOverview } from '@/features/teams/useTeams'

export interface TeamSelection {
  team?: string
  categoryId?: string
}

const CHIP =
  'inline-flex items-center gap-2 rounded-pill border px-3 py-1.5 text-sm transition ' +
  'hover:-translate-y-px hover:shadow-sm active:translate-y-0'

function Count({ n, selected }: { n: number; selected: boolean }) {
  return (
    <span
      className="rounded-pill px-1.5 text-xs font-semibold"
      style={{
        backgroundColor: selected
          ? 'color-mix(in srgb, var(--brand-on) 12%, transparent)'
          : 'var(--canvas)',
      }}
    >
      {n}
    </span>
  )
}

export function TeamBar({
  selection,
  onChange,
}: {
  selection: TeamSelection
  onChange: (next: TeamSelection) => void
}) {
  const { data, isPending, error } = useTeams()

  // A filter strip that fails should not take the page with it — the list below still works.
  if (error) return null
  if (isPending) return <Loading label="Loading teams" />

  const teams = data?.teams ?? []
  if (teams.length === 0) return null

  const active: TeamOverview | undefined = teams.find((t) => t.team === selection.team)
  const categories = active?.categories ?? []

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={CHIP}
          aria-pressed={!selection.team}
          onClick={() => onChange({})}
          style={
            !selection.team
              ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)', borderColor: 'var(--brand)' }
              : { borderColor: 'var(--border)', color: 'var(--fg)' }
          }
        >
          All teams
          <Count n={data?.totalProjects ?? 0} selected={!selection.team} />
        </button>

        {teams.map((team) => {
          const selected = selection.team === team.team
          return (
            <button
              key={team.team}
              type="button"
              className={CHIP}
              aria-pressed={selected}
              title={team.label}
              // Clicking the selected team clears it, so the bar is its own escape hatch.
              onClick={() => onChange(selected ? {} : { team: team.team })}
              style={
                selected
                  ? { backgroundColor: 'var(--brand)', color: 'var(--brand-on)', borderColor: 'var(--brand)' }
                  : { borderColor: 'var(--border)', color: 'var(--fg)' }
              }
            >
              {team.shortLabel}
              <Count n={team.projectCount} selected={selected} />
            </button>
          )
        })}
      </div>

      {active ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 pl-1">
          <span className="micro-label text-fg-muted">{active.label}</span>
          {categories.length === 0 ? (
            <span className="text-xs text-fg-muted">
              No categories yet — a Portfolio Manager can add them under Teams.
            </span>
          ) : (
            categories.map((category) => {
              const selected = selection.categoryId === category.id
              return (
                <button
                  key={category.id}
                  type="button"
                  aria-pressed={selected}
                  className="rounded-md border px-2.5 py-1 text-xs transition hover:bg-canvas"
                  onClick={() =>
                    onChange(
                      selected
                        ? { team: active.team }
                        : { team: active.team, categoryId: category.id },
                    )
                  }
                  style={
                    selected
                      ? {
                          borderColor: 'var(--brand)',
                          backgroundColor: 'color-mix(in srgb, var(--brand) 18%, transparent)',
                          color: 'var(--fg)',
                        }
                      : { borderColor: 'var(--border)', color: 'var(--fg-muted)' }
                  }
                >
                  {category.name} · {category.projectCount}
                </button>
              )
            })
          )}
          {/* Only IT Operations subdivides today. Saying so is friendlier than an empty row
              that looks like something failed to load. */}
          {active.categories.length === 0 ? null : (
            <Pill>{categories.reduce((sum, c) => sum + c.projectCount, 0)} categorised</Pill>
          )}
        </div>
      ) : null}
    </div>
  )
}
